/**
 * Sparkline pro sloupec v overview tabulce (14 dní CTR).
 *
 * Vědomě bez os a popisků — je to indikátor tvaru, ne graf ke čtení hodnot.
 * Přesná čísla jsou ve stejném řádku tabulky, takže relief je splněný
 * tabulkou samotnou.
 *
 * Kreslí se do canvasu ručně, ne přes ECharts: v tabulce jich může být 5 000
 * a 5 000 instancí ECharts by aplikaci položilo.
 */
import { useEffect, useRef } from 'react';
import { supplierColor, type SupplierKey } from './palette';
import { useTheme } from '@/hooks/useTheme';

export interface SparklineProps {
  values: number[];
  supplier?: SupplierKey;
  width?: number;
  height?: number;
  /** popisek pro čtečky — hodnoty jsou i v tabulce */
  ariaLabel?: string;
}

export function Sparkline({
  values,
  supplier = 'B',
  width = 84,
  height = 22,
  ariaLabel,
}: SparklineProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { mode } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const usable = values.filter((v) => Number.isFinite(v));
    if (usable.length < 2) return;

    const min = Math.min(...usable);
    const max = Math.max(...usable);
    const span = max - min || 1;
    const pad = 2;
    const stepX = (width - pad * 2) / (values.length - 1);

    const color = supplierColor(supplier, mode);

    ctx.beginPath();
    values.forEach((value, i) => {
      const x = pad + i * stepX;
      const y = height - pad - ((value - min) / span) * (height - pad * 2);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.4;
    ctx.lineJoin = 'round';
    ctx.stroke();

    // poslední bod zvýraznit — oko hledá „kde to skončilo"
    const last = values[values.length - 1];
    if (last !== undefined && Number.isFinite(last)) {
      const x = pad + (values.length - 1) * stepX;
      const y = height - pad - ((last - min) / span) * (height - pad * 2);
      ctx.beginPath();
      ctx.arc(x, y, 2, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    }
  }, [values, width, height, supplier, mode]);

  if (values.length < 2) {
    return <span className="sparkline__empty" title="Málo dat pro sparkline">—</span>;
  }

  return (
    <canvas
      ref={canvasRef}
      className="sparkline"
      role="img"
      aria-label={ariaLabel ?? `Vývoj za posledních ${values.length} dní`}
    />
  );
}
